import "./InvoiceContainer.scss";

import { Checkbox } from "components/inputs";
import type { Lien } from "pages/liens/LienTypes";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { invoiceAPI, useGetInvoiceByIdQuery } from "services/invoice-api";
import { useQuery } from "utils/hooks";

import InvoiceDetailForm from "./InvoiceDetailForm";
import InvoiceHistory from "./InvoiceHistory";
import type {
  InvoiceableEntity,
  InvoiceContainerConfig,
  InvoiceType,
} from "./InvoiceTypes";

type InvoiceContainerProps = {
  entity: InvoiceableEntity;
  config?: InvoiceContainerConfig;
  invoiceType?: InvoiceType["invoice_type"];
  availableLiens?: Lien[];
  applySwimColumnClass?: boolean;
};

type NoInvoiceRequiredCheckboxProps = {
  checked: boolean;
  onChange: (value: boolean) => void;
};

// used with Amended Review Screening service
const NoInvoiceRequiredCheckbox = ({
  checked,
  onChange,
}: NoInvoiceRequiredCheckboxProps) => (
  <div className="invoice-section__pre-header">
    <Checkbox
      name="no_invoice_required"
      className="invoice-section__checkbox"
      label="No Invoice Required"
      checked={checked}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.checked)
      }
    />
  </div>
);

/**
 * InvoiceContainer displays invoice history and details for an invoiceable "entity".
 * It is used in the ConsultingPage, Lien, Client, and FutureMedicalPage components.
 *
 * Entities are shaped in invoiceAdapter.ts
 */
const InvoiceContainer = ({
  entity,
  config = {
    showHistory: true,
    showCMS: false,
  },
  invoiceType = "file_service",
  availableLiens = [],
  applySwimColumnClass = true,
}: InvoiceContainerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const urlInvoiceId = query.get("invoiceId");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    urlInvoiceId,
  );

  useEffect(() => {
    if (!urlInvoiceId && selectedInvoiceId) {
      invoiceAPI.util.invalidateTags([
        { type: "Invoice", id: parseInt(selectedInvoiceId) },
      ]);
    }
    setSelectedInvoiceId(urlInvoiceId);
  }, [urlInvoiceId, selectedInvoiceId]);

  const { data: selectedInvoice } = useGetInvoiceByIdQuery(
    { id: selectedInvoiceId },
    {
      skip: !selectedInvoiceId,
      refetchOnMountOrArgChange: true,
    },
  );

  const handleInvoiceSelect = useCallback(
    (id: number) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("invoiceId", id.toString());
      navigate({ search: searchParams.toString() });
    },
    [navigate, location.search],
  );

  if (!entity) {
    console.warn("InvoiceContainer: entity is undefined");
    return null;
  }

  const shouldShowDetailForm =
    !!selectedInvoice &&
    !!selectedInvoiceId &&
    config.no_invoice_required !== true &&
    selectedInvoice?.id === parseInt(selectedInvoiceId);

  const shouldShowInvoiceHistory =
    config.showHistory && config.no_invoice_required !== true;

  return (
    <div className={applySwimColumnClass ? "swim-column" : ""}>
      {/* this is only true for Amended Review Screening service */}
      {config.no_invoice_required !== undefined && (
        <NoInvoiceRequiredCheckbox
          checked={config.no_invoice_required}
          onChange={config.onNoInvoiceRequiredChange ?? (() => {})}
        />
      )}
      {shouldShowInvoiceHistory && (
        <InvoiceHistory
          entity={entity}
          onInvoiceSelect={handleInvoiceSelect}
          selectedInvoiceId={
            selectedInvoiceId ? parseInt(selectedInvoiceId) : undefined
          }
          invoiceType={invoiceType}
          showCMS={config.showCMS}
          filterCMSInvoices={config.filterCMSInvoices}
        />
      )}
      {shouldShowDetailForm && (
        <InvoiceDetailForm
          key={`invoice-detail-${selectedInvoice.id}`}
          invoice={selectedInvoice}
          entity={entity}
          config={config}
          availableLiens={availableLiens}
        />
      )}
    </div>
  );
};

export default InvoiceContainer;
